import {
  ISelectedMember,
  QuoteType,
} from '@/types';
import {
  IRhiV2Journey,
  IRhiV2Plan,
  IRhiV2UnavailablePlan,
  IRhiV2QuoteRequest,
  IRhiV2QuoteResponse,
  IRhiV2EffectiveParams,
} from '@/types/rhiV2';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRhiV2Quote,
  generateQuote,
  getRhiV2JourneyList,
  getRhiV2Quote,
  QuoteApiError,
} from '@/queries/retail-health-insurance.queries';
import { getMemberAndDependentsDetails } from '@/queries/memberDetails.queries';
import {
  RHI_QUERY_KEYS,
  RHI_SESSION_KEYS,
  RHI_TIMEOUT_VALUES,
} from '@/constants/queryKeys';
import { isAgentLaunch } from '@/utils/agentSession';
import { useLocation, useNavigate } from 'react-router-dom';
import useSessionStorage from '@/hooks/useSessionStorage';
import useRhiVersion from '@/hooks/useRhiVersion';
import { useShowRhiV2Error } from '@/hooks/useShowRhiV2Error';
import {
  isTransientError,
  retryOnTransientError,
} from '@/utils/axios/baseAxiosInstance';
import {
  isRhiV2QuoteLocked,
  resolveRhiV2Error,
  rhiV2ErrorCode,
  rhiV2ErrorMessage,
  rhiV2PlanRefusal,
  toastRhiV2Error,
} from '@/utils/rhiV2Error';
import {
  getJourneyPath,
  getJourneyStatus,
  HEALTH_INSURANCE_BASE_PATH,
  isPaidJourney,
  markResumeReturn,
  returnsToPlanList,
  RHI_V2_AGENT_PATH,
  RHI_V2_NEW_QUOTE_PATH,
  RHI_V2_PLANS_PATH,
} from '@/utils/rhiV2/journey';
import { rhiV2PlanToQuote } from '@/utils/rhiV2/plans';
import { useAppContext } from '@/context/AppContext';
import {
  RHI_PHONE_NUMBER,
  RHI_V2_ERROR_CODES,
  RHI_WHATSAPP_INITIAL_MESSAGE,
} from '@/constants/healthInsuranceConfig';
import {
  RHI_V2_ERROR_COPY,
  RHI_V2_PLAN_LIST_CHANGED_NOTICE,
  RHI_V2_PLAN_REFRESHED_NOTICE,
} from '@/constants/rhiV2ErrorCopy';
import { toastRhiV2Notice } from '@/utils/rhiV2NoticeToast';
import { whatsAppMessageUtil } from '@/utils/whatsAppMessageUtil';

const DEFAULT_SUM_INSURED = 1000000;
const DEFAULT_SELECTED_MEMBERS: ISelectedMember[] = [];
const MAX_CACHED_RIDER_QUOTES = 10;

// Define the shape of your context, now including relationType
interface ICreateQuoteOptions {
  /** The new quote is ready to show on the plan list. */
  onFreshQuote?: () => void;
  /** The server refused the members (`RHI_V2_INVALID_QUOTE_REQUEST`); gets its reason instead of the error page. */
  onInvalidRequest?: (message: string) => void;
}

interface HealthInsuranceContext {
  leadId: string;
  setLeadId: React.Dispatch<string>;
  quoteId: string;
  setQuoteId: React.Dispatch<string>;
  quotePlans: IRhiV2Plan[];
  quoteUnavailablePlans: IRhiV2UnavailablePlan[];
  quoteRequest: IRhiV2QuoteRequest | null;
  effectiveParams: IRhiV2EffectiveParams | null;
  createQuote: (
    request: IRhiV2QuoteRequest,
    options?: ICreateQuoteOptions,
  ) => void;
  isCreatingQuote: boolean;
  benefitId: string;
  setBenefitId: React.Dispatch<string>;
  quotes: QuoteType[];
  setQuotes: (quotes: QuoteType[], riders?: Array<string>) => void;
  essentialRidersIncluded: boolean;
  setEssentialRidersIncluded: React.Dispatch<boolean>;
  selectedMembers: ISelectedMember[];
  proposalId: string;
  setProposalId: React.Dispatch<React.SetStateAction<string>>;
  includedRiders: Array<string>;
  setIncludedRiders: React.Dispatch<React.SetStateAction<Array<string>>>;
  setSelectedMembers: React.Dispatch<ISelectedMember[]>;
  isLoadingQuotes: boolean;
  isFetchingQuotes: boolean;
  quotesError: any;
  refetchQuotes: () => void;
  selectedSumInsured: number;
  setSelectedSumInsured: React.Dispatch<number>;
  localQuotes: QuoteType[];
  setLocalQuotes: React.Dispatch<React.SetStateAction<QuoteType[]>>;
  premierPrice: string;
  clearLocalQuotes: () => void;
  selectedPlanId: string;
  setSelectedPlanId: React.Dispatch<string>;
  selectedQuote?: QuoteType;
  markRiderQuoteFresh: (params: {
    planIds: Array<string>;
    sumInsured: number;
    includeEssentialRiders: boolean;
    riders: Array<string>;
    quotes: QuoteType[];
  }) => void;
  isRiderQuoteFresh: (params: {
    planIds: Array<string>;
    sumInsured: number;
    includeEssentialRiders: boolean;
    riders: Array<string>;
  }) => boolean;
  getCachedRiderQuote: (params: {
    planIds: Array<string>;
    sumInsured: number;
    includeEssentialRiders: boolean;
    riders: Array<string>;
  }) => QuoteType[] | undefined;
  invalidateRiderQuoteFreshness: () => void;
  resetQuoteCache: () => void;
  resumeJourney: (journey: IRhiV2Journey) => void;
  routeToJourney: (
    journey: IRhiV2Journey,
    options?: { replace?: boolean; fromResume?: boolean },
  ) => void;
  handleQuoteLocked: (lockedQuoteId: string) => Promise<void>;
  /**
   * Routes a plan id that can't be used (or saved add-ons that can't be priced) from any
   * plan-scoped call; `retry` repeats that call while the plan list is being re-quoted.
   * True when the error was one of these and has been handled.
   */
  handlePlanCode: (error: unknown, retry?: () => void) => boolean;
  /** The plan id a re-quote carried the journey to: its new draft has no documents yet. */
  carriedPlanId: string;
  setCarriedPlanId: React.Dispatch<string>;
}

// Create the context with default values
const HealthInsuranceContext = createContext<
  HealthInsuranceContext | undefined
>(undefined);

const EMPTY_QUOTES: QuoteType[] = [];
/** Guide 2026-10-14 §2: PLAN_LIST_UPDATING is repeated after retryAfterMs, at most 6 times. */
const PLAN_LIST_UPDATING_MAX_TRIES = 6;
const PLAN_LIST_UPDATING_RETRY_MS = 5000;
/** Keeps "being updated" up while the repeated call is in flight; it closes soon after one succeeds. */
const PLAN_LIST_UPDATING_NOTICE_EXTRA_MS = 2000;

const EMPTY_PLANS: IRhiV2Plan[] = [];
const EMPTY_UNAVAILABLE_PLANS: IRhiV2UnavailablePlan[] = [];
/**
 * Guide 2026-10-20 (a KYC correction keeps the plan id) §3.2: after a correction, reading the quote
 * re-quotes the lead and gives every plan a new id, the one being bought included. So it's read
 * only where plans are on screen: the plan list, select members (which waits for the plans before
 * opening the list), and the v1 screens built on them. Not on home, the agent landing, error or
 * thank-you, nor anywhere from the add-on page onwards.
 */
const RHI_V2_PLAN_LIST_SCREEN =
  /^\/retail\/health-insurance\/(plans|select-member|recommendation|plans-benefits|add-riders\/[^/]+|verify-details|add-nominee|member-details(\/[^/]+)?|checkout|bank-details)\/?$/;

// Create a provider component
export const HealthInsuranceContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname, key: locationKey } = useLocation();
  const { isWebView } = useAppContext();
  const isV1 = useRhiVersion() === 'v1';
  const isPlanListScreen = RHI_V2_PLAN_LIST_SCREEN.test(pathname);
  // Changes on every arrival at the plan list, including coming back to it from another page.
  const planListVisitKey =
    pathname.replace(/\/$/, '') === RHI_V2_PLANS_PATH ? locationKey : '';

  const [benefitId, setBenefitId] = useSessionStorage<string>(RHI_SESSION_KEYS.BENEFIT_ID, '');
  const [leadId, setLeadId] = useSessionStorage<string>(RHI_SESSION_KEYS.LEAD_ID, '');
  const [quoteId, setQuoteId] = useSessionStorage<string>(
    RHI_SESSION_KEYS.QUOTE_ID,
    '',
  );
  const [quoteRequest, setQuoteRequest] =
    useSessionStorage<IRhiV2QuoteRequest | null>(
      RHI_SESSION_KEYS.QUOTE_REQUEST,
      null,
    );
  const [effectiveParams, setEffectiveParams] =
    useSessionStorage<IRhiV2EffectiveParams | null>(
      RHI_SESSION_KEYS.EFFECTIVE_PARAMS,
      null,
    );
  const [selectedPlanId, setSelectedPlanId] = useSessionStorage<string>(RHI_SESSION_KEYS.SELECTED_PLAN_ID, '');
  const [selectedMembers, setSelectedMembers] = useSessionStorage<ISelectedMember[]>(RHI_SESSION_KEYS.SELECTED_MEMBERS, DEFAULT_SELECTED_MEMBERS);
  const [selectedSumInsured, setSelectedSumInsured] = useSessionStorage<number>(RHI_SESSION_KEYS.SELECTED_SUM_INSURED, DEFAULT_SUM_INSURED);
  const [essentialRidersIncluded, setEssentialRidersIncluded] = useSessionStorage<boolean>(RHI_SESSION_KEYS.ESSENTIAL_RIDERS, false);

  const [proposalIdState, setProposalIdState] = useSessionStorage<string>(RHI_SESSION_KEYS.PROPOSAL_ID, '');
  const [carriedPlanId, setCarriedPlanId] = useSessionStorage<string>(RHI_SESSION_KEYS.CARRIED_PLAN_ID, '');
  const [includedRiders, setIncludedRiders] = useState<Array<string>>([]);
  const [localQuotes, setLocalQuotes] = useState<QuoteType[]>([]);
  const [latestRiderQuoteKey, setLatestRiderQuoteKey] = useState<string>('');
  const riderQuoteCacheRef = useRef<Map<string, QuoteType[]>>(new Map());

  const queryClient = useQueryClient();

  const hasSelectedPlan = !!selectedPlanId;
  const [hasQuotePollTimedOut, setHasQuotePollTimedOut] = useState(false);

  useEffect(() => {
    setHasQuotePollTimedOut(false);
    if (!quoteId) return;

    const pollTimeout = setTimeout(
      () => setHasQuotePollTimedOut(true),
      RHI_TIMEOUT_VALUES.RHI_V2_QUOTE_POLL_TIMEOUT_MS,
    );

    return () => clearTimeout(pollTimeout);
  }, [quoteId, planListVisitKey]);

  const quoteQueryOptions = getRhiV2Quote(quoteId ?? '');

  const {
    data: quoteResponse,
    isLoading: isQuoteResponseLoading,
    isFetching: isFetchingV2Quotes,
    error: v2QuotesError,
    refetch: refetchV2Quotes,
  } = useQuery<IRhiV2QuoteResponse>({
    ...quoteQueryOptions,
    enabled: !isV1 && quoteQueryOptions.enabled && isPlanListScreen,
    refetchInterval: hasQuotePollTimedOut
      ? false
      : quoteQueryOptions.refetchInterval,
    staleTime: hasSelectedPlan ? Infinity : 5 * 60 * 1000,
    refetchOnMount: hasSelectedPlan ? false : true,
    refetchOnReconnect: hasSelectedPlan ? false : true,
  });

  const v2Quotes = useMemo(
    () =>
      quoteResponse
        ? quoteResponse.plans.map((plan) =>
            rhiV2PlanToQuote(plan, quoteResponse.plumQuoteId),
          )
        : EMPTY_QUOTES,
    [quoteResponse],
  );

  const isLoadingV2Quotes =
    isQuoteResponseLoading ||
    (quoteResponse?.status === 'IN_PROGRESS' && !hasQuotePollTimedOut);

  const generateQuoteOptions = generateQuote(
    leadId ?? '',
    selectedSumInsured ?? DEFAULT_SUM_INSURED,
    essentialRidersIncluded ?? false,
    {
      retry: 2,
      retryDelay: 1000,
      throwOnError: false,
    },
    []
  );

  const {
    data: v1Quotes = EMPTY_QUOTES,
    isLoading: isLoadingV1Quotes,
    isFetching: isFetchingV1Quotes,
    error: v1QuotesError,
    refetch: refetchV1Quotes,
  } = useQuery<QuoteType[]>({
    ...generateQuoteOptions,
    queryKey: [RHI_QUERY_KEYS.GENERATE_QUOTE, leadId, selectedSumInsured, essentialRidersIncluded, RHI_QUERY_KEYS.PERSIST],
    staleTime: hasSelectedPlan ? Infinity : 5 * 60 * 1000,
    refetchOnMount: hasSelectedPlan ? false : true,
    refetchOnReconnect: hasSelectedPlan ? false : true,
    enabled: isV1 && generateQuoteOptions.enabled,
  });

  const quotes = isV1 ? v1Quotes : v2Quotes;
  const isLoadingQuotes = isV1 ? isLoadingV1Quotes : isLoadingV2Quotes;
  const isFetchingQuotes = isV1 ? isFetchingV1Quotes : isFetchingV2Quotes;
  const quotesError = isV1 ? v1QuotesError : v2QuotesError;
  const refetchQuotes = isV1 ? refetchV1Quotes : refetchV2Quotes;

  const quotePlans = quoteResponse?.plans ?? EMPTY_PLANS;
  const quoteUnavailablePlans =
    quoteResponse?.notAvailable ?? EMPTY_UNAVAILABLE_PLANS;

  const latestQuoteRequestRef = useRef<IRhiV2QuoteRequest | null>(null);
  const shownQuoteErrorRef = useRef<unknown>();
  const quoteIdRef = useRef(quoteId);
  const quoteRequestRef = useRef(quoteRequest);
  quoteIdRef.current = quoteId;
  quoteRequestRef.current = quoteRequest;
  const previousQuoteRef = useRef<{
    quoteId: string;
    request: IRhiV2QuoteRequest | null;
  }>({ quoteId: '', request: null });
  const invalidRequestRef = useRef<{
    request: IRhiV2QuoteRequest;
    onInvalidRequest: (message: string) => void;
  } | null>(null);

  const showRhiError = useShowRhiV2Error();

  const showQuoteError = useCallback(
    (error: unknown) => {
      const copy = resolveRhiV2Error(error);
      // The agent fixes the details from what the server refused, so show its reason.
      const reason =
        isAgentLaunch() && copy.code === RHI_V2_ERROR_CODES.INVALID_QUOTE_REQUEST
          ? rhiV2ErrorMessage(error)
          : undefined;
      showRhiError(error, reason ? { ...copy, body: reason } : copy);
    },
    [showRhiError],
  );

  const createQuoteMutation = useMutation({
    ...createRhiV2Quote(),
    retry: retryOnTransientError,
    retryDelay: 1000,
    // Every POST /quotes prices a new quote: always the plan list for its plumQuoteId.
    onSuccess: ({ plumQuoteId, effectiveParams: params }, request) => {
      if (
        latestQuoteRequestRef.current &&
        request !== latestQuoteRequestRef.current
      )
        return;
      setQuoteId(plumQuoteId);
      setEffectiveParams(params ?? null);
    },
    onError: (error, request) => {
      if (
        latestQuoteRequestRef.current &&
        request !== latestQuoteRequestRef.current
      )
        return;

      const { quoteId: previousQuoteId, request: previousRequest } =
        previousQuoteRef.current;
      const invalid = invalidRequestRef.current;
      const invalidReason =
        invalid?.request === request &&
        rhiV2ErrorCode(error) === RHI_V2_ERROR_CODES.INVALID_QUOTE_REQUEST
          ? rhiV2ErrorMessage(error)
          : undefined;

      if (previousQuoteId) {
        latestQuoteRequestRef.current = previousRequest;
        setQuoteId(previousQuoteId);
        setQuoteRequest(previousRequest);
      }

      if (invalid && invalidReason) {
        invalid.onInvalidRequest(invalidReason);
        return;
      }

      if (!previousQuoteId) {
        showQuoteError(error);
        return;
      }

      toastRhiV2Error(
        error,
        'Unable to update plans. Please try again.',
        'rhi-quote-update-error',
      );
    },
  });

  const { mutate: createQuoteMutate } = createQuoteMutation;

  // Back on the plan list from any page: read the quote again and poll it as on first arrival,
  // rather than show what was cached when they left. A quote being created fetches on its own.
  const isCreatingQuoteRef = useRef(false);
  isCreatingQuoteRef.current = createQuoteMutation.isPending;
  useEffect(() => {
    if (!planListVisitKey || !quoteIdRef.current || isCreatingQuoteRef.current) return;
    refetchV2Quotes();
  }, [planListVisitKey, refetchV2Quotes]);

  const createQuote = useCallback(
    (
      request: IRhiV2QuoteRequest,
      { onFreshQuote, onInvalidRequest }: ICreateQuoteOptions = {},
    ) => {
      invalidRequestRef.current = onInvalidRequest
        ? { request, onInvalidRequest }
        : null;
      if (quoteIdRef.current) {
        previousQuoteRef.current = {
          quoteId: quoteIdRef.current,
          request: quoteRequestRef.current ?? null,
        };
      }
      latestQuoteRequestRef.current = request;
      setQuoteId('');
      setQuoteRequest(request);
      setEffectiveParams(null);
      createQuoteMutate(request, { onSuccess: () => onFreshQuote?.() });
    },
    [createQuoteMutate, setQuoteId, setQuoteRequest, setEffectiveParams],
  );


  useEffect(() => {
    if (!benefitId || isAgentLaunch()) return;

    queryClient.prefetchQuery(getMemberAndDependentsDetails(benefitId));
  }, [benefitId, queryClient]);

  useEffect(() => {
    if (!isV1) return;

    const isInsurerApiError =
      v1QuotesError instanceof QuoteApiError ||
      (typeof v1QuotesError === 'object' &&
        v1QuotesError !== null &&
        (v1QuotesError as { code?: string }).code === 'INSURER_API_ERROR');

    const isTransient = isTransientError(v1QuotesError);

    if (v1QuotesError && leadId && !isInsurerApiError && !isTransient) {
      navigate('/retail/health-insurance/error', { replace: true });
    } else if (
      v1Quotes &&
      v1Quotes.length === 0 &&
      !isLoadingV1Quotes &&
      leadId &&
      !isInsurerApiError &&
      !isTransient
    ) {
      navigate('/retail/health-insurance/error', { replace: true });
    }
  }, [isV1, v1QuotesError, v1Quotes, isLoadingV1Quotes, leadId, navigate]);

  const selectedQuote = useMemo(() => {
    if (!quotes || quotes.length === 0) {
      return undefined;
    }

    if (selectedPlanId) {
      const matchingQuote = quotes.find(
        (quote) => quote.planId === selectedPlanId,
      );
      if (matchingQuote) {
        return matchingQuote;
      }
    }

    return quotes[0];
  }, [quotes, selectedPlanId]);

  const getRiderQuoteKey = useCallback(
    ({
      planIds,
      sumInsured,
      includeEssentialRiders,
      riders,
    }: {
      planIds: Array<string>;
      sumInsured: number;
      includeEssentialRiders: boolean;
      riders: Array<string>;
    }) => {
      const sortedRiders = [...riders].sort();
      const sortedPlanIds = [...planIds].sort();
      return [
        sortedPlanIds.join('|'),
        sumInsured,
        includeEssentialRiders ? '1' : '0',
        sortedRiders.join('|'),
      ].join('::');
    },
    [],
  );

  const markRiderQuoteFresh = useCallback(
    (params: {
      planIds: Array<string>;
      sumInsured: number;
      includeEssentialRiders: boolean;
      riders: Array<string>;
      quotes: QuoteType[];
    }) => {
      const { quotes: freshQuotes, ...cacheKeyParams } = params;
      const riderQuoteKey = getRiderQuoteKey(cacheKeyParams);
      const riderQuoteCache = riderQuoteCacheRef.current;

      if (riderQuoteCache.has(riderQuoteKey)) {
        riderQuoteCache.delete(riderQuoteKey);
      }

      riderQuoteCache.set(riderQuoteKey, freshQuotes);

      while (riderQuoteCache.size > MAX_CACHED_RIDER_QUOTES) {
        const oldestRiderQuoteKey = riderQuoteCache.keys().next().value;
        if (!oldestRiderQuoteKey) {
          break;
        }
        riderQuoteCache.delete(oldestRiderQuoteKey);
      }

      setLatestRiderQuoteKey(riderQuoteKey);
    },
    [getRiderQuoteKey],
  );

  const isRiderQuoteFresh = useCallback(
    (params: {
      planIds: Array<string>;
      sumInsured: number;
      includeEssentialRiders: boolean;
      riders: Array<string>;
    }) => {
      const riderQuoteKey = getRiderQuoteKey(params);
      return (
        latestRiderQuoteKey === riderQuoteKey ||
        riderQuoteCacheRef.current.has(riderQuoteKey)
      );
    },
    [getRiderQuoteKey, latestRiderQuoteKey],
  );

  const getCachedRiderQuote = useCallback(
    (params: {
      planIds: Array<string>;
      sumInsured: number;
      includeEssentialRiders: boolean;
      riders: Array<string>;
    }) => {
      return riderQuoteCacheRef.current.get(getRiderQuoteKey(params));
    },
    [getRiderQuoteKey],
  );

  const invalidateRiderQuoteFreshness = useCallback(() => {
    riderQuoteCacheRef.current.clear();
    setLatestRiderQuoteKey('');
  }, []);

  const premierPrice = useMemo(() => {
    if (!selectedPlanId) return '';

    const source = localQuotes.length > 0 ? localQuotes : quotes;
    const quote = source.find((q) => q.planId === selectedPlanId);
    return quote?.premium?.currencyNativeStringWithoutDecimal || '';
  }, [selectedPlanId, quotes, localQuotes]);

  const setQuotes = useCallback((quotes: QuoteType[], riders?: Array<string>) => {
    if (!quotes || quotes.length === 0) {
      navigate('/retail/health-insurance/error', { replace: true });
      return;
    }

    const mainQueryKey = [RHI_QUERY_KEYS.GENERATE_QUOTE, leadId, selectedSumInsured, essentialRidersIncluded, RHI_QUERY_KEYS.PERSIST];

    queryClient.setQueryData(mainQueryKey, quotes);

    if (riders || includedRiders.length > 0) {
      const ridersQueryKey = [
        RHI_QUERY_KEYS.RETAIL_HEALTH_INSURANCE_QUOTES,
        leadId,
        selectedSumInsured,
        essentialRidersIncluded,
        riders || includedRiders,
        undefined,
      ];
      queryClient.setQueryData(ridersQueryKey, quotes);
    }
  }, [
    navigate,
    leadId,
    selectedSumInsured,
    essentialRidersIncluded,
    queryClient,
    includedRiders,
  ]);

  const clearLocalQuotes = useCallback(() => {
    setLocalQuotes([]);
  }, []);

  const resetQuoteCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: [RHI_QUERY_KEYS.GENERATE_QUOTE] });
    queryClient.removeQueries({ queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_QUOTE] });
    queryClient.removeQueries({ queryKey: [RHI_QUERY_KEYS.RETAIL_HEALTH_INSURANCE_QUOTES] });
    invalidateRiderQuoteFreshness();
    clearLocalQuotes();
  }, [queryClient, invalidateRiderQuoteFreshness, clearLocalQuotes]);

  const setProposalId: React.Dispatch<React.SetStateAction<string>> =
    useCallback(
      (value) => {
        setProposalIdState((prev) =>
          value instanceof Function ? value(prev ?? '') : value,
        );
      },
      [setProposalIdState],
    );

  const resumeJourney = useCallback(
    (journey: IRhiV2Journey) => {
      resetQuoteCache();
      setQuoteRequest(null);
      setEffectiveParams(null);
      setQuoteId(journey.quoteId);
      setSelectedPlanId(journey.planId ? String(journey.planId) : '');
      setProposalIdState(journey.proposalId ?? '');
    },
    [
      resetQuoteCache,
      setQuoteRequest,
      setEffectiveParams,
      setQuoteId,
      setSelectedPlanId,
      setProposalIdState,
    ],
  );

  const routeToJourney = useCallback(
    (
      journey: IRhiV2Journey,
      { replace = false, fromResume = false } = {},
    ) => {
      const path = getJourneyPath(journey);
      if (!path) {
        whatsAppMessageUtil(
          RHI_PHONE_NUMBER,
          RHI_WHATSAPP_INITIAL_MESSAGE,
          isWebView,
        );
        return;
      }

      // Backing out of a resumed, unpaid stage lands on its plan list: mark the
      // page being left, so returning to it redirects there.
      if (fromResume && returnsToPlanList(journey)) {
        markResumeReturn(locationKey, RHI_V2_PLANS_PATH);
      }

      const isNewQuote = path === RHI_V2_NEW_QUOTE_PATH;
      if (!isNewQuote) resumeJourney(journey);
      window.scrollTo({ top: 0, behavior: 'instant' });
      // Agents can't open select-member; their new quote starts from their landing.
      navigate(isNewQuote && isAgentLaunch() ? RHI_V2_AGENT_PATH : path, {
        replace,
      });
    },
    [isWebView, locationKey, navigate, resumeJourney],
  );

  const planListUpdatingTriesRef = useRef(0);
  const planListUpdatingRetryRef = useRef<ReturnType<typeof setTimeout>>();

  // A waiting repeat belongs to the page and quote it was made on: once the customer leaves,
  // it must not commit the earlier call.
  useEffect(
    () => () => clearTimeout(planListUpdatingRetryRef.current),
    [pathname, quoteId],
  );

  // Guide 2026-10-14 §2: route on the code, never the message.
  const handlePlanCode = useCallback(
    (error: unknown, retry?: () => void) => {
      const refusal = rhiV2PlanRefusal(error);
      if (!refusal) return false;
      if (refusal.kind !== 'updating') planListUpdatingTriesRef.current = 0;

      switch (refusal.kind) {
        case 'replaced':
          // Same plan, new id: drop the old proposal and choose again on the new id's plan page.
          if (!refusal.planId) break;
          setSelectedPlanId(String(refusal.planId));
          setProposalIdState('');
          setCarriedPlanId(String(refusal.planId));
          toastRhiV2Notice(RHI_V2_PLAN_REFRESHED_NOTICE);
          navigate(`${RHI_V2_PLANS_PATH}/${refusal.planId}`, { replace: true });
          return true;
        case 'updating':
          // The list is being re-quoted: repeat the same call, at most 6 times (~30 s).
          planListUpdatingTriesRef.current += 1;
          if (
            retry &&
            planListUpdatingTriesRef.current <= PLAN_LIST_UPDATING_MAX_TRIES
          ) {
            const retryAfterMs = refusal.retryAfterMs ?? PLAN_LIST_UPDATING_RETRY_MS;
            toastRhiV2Notice(
              { title: RHI_V2_ERROR_COPY[RHI_V2_ERROR_CODES.PLAN_LIST_UPDATING].title },
              {
                kind: 'loading',
                durationMs: retryAfterMs + PLAN_LIST_UPDATING_NOTICE_EXTRA_MS,
              },
            );
            clearTimeout(planListUpdatingRetryRef.current);
            planListUpdatingRetryRef.current = setTimeout(retry, retryAfterMs);
            return true;
          }
          planListUpdatingTriesRef.current = 0;
          break;
        case 'gone':
          toastRhiV2Notice(
            { title: RHI_V2_ERROR_COPY[refusal.code].title },
            { kind: 'error' },
          );
          navigate(RHI_V2_PLANS_PATH, { replace: true });
          return true;
        case 'addons':
          // The saved add-ons can't be priced as they stand: adjust them on the plan page.
          toastRhiV2Notice(
            { title: RHI_V2_ERROR_COPY[refusal.code].title },
            { kind: 'error' },
          );
          navigate(
            selectedPlanId ? `${RHI_V2_PLANS_PATH}/${selectedPlanId}` : RHI_V2_PLANS_PATH,
            { replace: true },
          );
          return true;
        default:
          break;
      }
      toastRhiV2Notice(RHI_V2_PLAN_LIST_CHANGED_NOTICE);
      navigate(RHI_V2_PLANS_PATH, { replace: true });
      return true;
    },
    [navigate, selectedPlanId, setSelectedPlanId, setProposalIdState, setCarriedPlanId],
  );

  const handleQuoteLocked = useCallback(
    async (lockedQuoteId: string) => {
      const journey = await queryClient
        .fetchQuery({ ...getRhiV2JourneyList(), staleTime: 0 })
        .then(({ journeys }) =>
          journeys.find((item) => item.quoteId === lockedQuoteId),
        )
        .catch(() => undefined);

      if (journey) {
        // An expired quote that can still be paid, or a payment still going through, lands
        // here too: only a paid journey says so; the rest say what their card says.
        toastRhiV2Notice({
          title: isPaidJourney(journey)
            ? RHI_V2_ERROR_COPY[RHI_V2_ERROR_CODES.QUOTE_LOCKED].title
            : getJourneyStatus(journey).title,
        });
        routeToJourney(journey, { replace: true });
        return;
      }
      // Not in the list (or the list didn't load): say so here, and Try again looks it up again.
      showRhiError(undefined, RHI_V2_ERROR_COPY[RHI_V2_ERROR_CODES.QUOTE_LOCKED]);
    },
    [queryClient, routeToJourney, showRhiError],
  );

  useEffect(() => {
    const isInsurerApiError =
      v2QuotesError instanceof QuoteApiError ||
      (typeof v2QuotesError === 'object' &&
        v2QuotesError !== null &&
        (v2QuotesError as { code?: string }).code === 'INSURER_API_ERROR');

    const isTransient = isTransientError(v2QuotesError);

    if (!v2QuotesError || !quoteId || isInsurerApiError || isTransient) return;
    // Home doesn't need the quote; a failure there mustn't send the customer away from it.
    if (pathname === HEALTH_INSURANCE_BASE_PATH) return;
    // Each failure is shown once: leaving its error screen (e.g. Go to home) doesn't bring
    // it back. A new failure, after Try again, is a new error and shows.
    if (shownQuoteErrorRef.current === v2QuotesError) return;

    shownQuoteErrorRef.current = v2QuotesError;
    // Paid, or expired but still payable: its journey card says where to go.
    if (isRhiV2QuoteLocked(v2QuotesError)) {
      handleQuoteLocked(quoteId);
      return;
    }
    showQuoteError(v2QuotesError);
  }, [v2QuotesError, quoteId, showQuoteError, pathname, handleQuoteLocked]);


  return (
    <HealthInsuranceContext.Provider
      value={{
        includedRiders,
        setIncludedRiders,
        selectedMembers: selectedMembers ?? [],
        setSelectedMembers,
        essentialRidersIncluded: essentialRidersIncluded ?? false,
        setEssentialRidersIncluded,
        benefitId: benefitId ?? '',
        setBenefitId,
        leadId: leadId ?? '',
        setLeadId,
        quoteId: quoteId ?? '',
        setQuoteId,
        quotePlans,
        quoteUnavailablePlans,
        quoteRequest: quoteRequest ?? null,
        effectiveParams: effectiveParams ?? null,
        createQuote,
        isCreatingQuote: createQuoteMutation.isPending,
        proposalId: proposalIdState ?? '',
        setProposalId,
        quotes,
        setQuotes,
        isLoadingQuotes,
        isFetchingQuotes,
        quotesError,
        refetchQuotes,
        selectedSumInsured: selectedSumInsured ?? DEFAULT_SUM_INSURED,
        setSelectedSumInsured,
        localQuotes,
        setLocalQuotes,
        premierPrice,
        clearLocalQuotes,
        selectedPlanId: selectedPlanId ?? '',
        setSelectedPlanId,
        selectedQuote,
        markRiderQuoteFresh,
        isRiderQuoteFresh,
        getCachedRiderQuote,
        invalidateRiderQuoteFreshness,
        resetQuoteCache,
        resumeJourney,
        routeToJourney,
        handleQuoteLocked,
        handlePlanCode,
        carriedPlanId: carriedPlanId ?? '',
        setCarriedPlanId,
      }}
    >
      {children}
    </HealthInsuranceContext.Provider>
  );
};

// Create a custom hook for easier access
export const useHealthInsuranceContext = () => {
  const context = useContext(HealthInsuranceContext);
  if (!context) {
    throw new Error(
      'useRelationContext must be used within a RelationProvider',
    );
  }
  return context;
};
